import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ORDER_URL,
  END_POINT_PRODUCT,
  END_POINT_USERS_URL,
  END_POINT_QUEUE,
} from "../../common/apiUrls";
import api from "../../utils/axiosUtils";

// Async thunks
export const fetchOrders = createAsyncThunk(
  "quickOrder/fetchOrders",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(ORDER_URL, { orderId });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const searchOrders = createAsyncThunk(
  "quickOrder/searchOrders",
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ORDER_URL}/search`, searchParams);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchUsers = createAsyncThunk(
  "quickOrder/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(END_POINT_USERS_URL);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addOrder = createAsyncThunk(
  "quickOrder/addOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post(ORDER_URL, orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addQuickOrder = createAsyncThunk(
  "quickOrder/addQuickOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post(`${ORDER_URL}/quick/baking`, orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// The active queue (not done, not being mixed), oldest first: the same list
// the Queue screen works from.
export const fetchQueue = createAsyncThunk(
  "quickOrder/fetchQueue",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${END_POINT_QUEUE}/all`, { limit: 50 });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// A new status for a queue entry, then the queue again (Delivered and
// Cancelled entries drop off it).
export const updateQueueStatus = createAsyncThunk(
  "quickOrder/updateQueueStatus",
  async ({ queueId, status }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.put(END_POINT_QUEUE, { queueId, status });
      dispatch(fetchQueue());
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchProducts = createAsyncThunk(
  "quickOrder/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(END_POINT_PRODUCT + "/all");
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  users: [],
  orders: [],
  currentOrder: null,
  orderResponse: null,
  quickOrder: null,
  products: [],
  queue: [],
  queueLoading: false,
  status: {
    loading: false,
    error: null,
  },
};

const quickOrderSlice = createSlice({
  name: "quickOrder",
  initialState,
  reducers: {
    resetError: (state) => {
      state.status.error = null;
      state.status.loading = false;
      state.currentOrder = null;
      state.orderResponse = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
        state.currentOrder = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.status.loading = false;
        state.currentOrder = action.payload.result;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
        state.currentOrder = null;
      })
      // Search Orders
      .addCase(searchOrders.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(searchOrders.fulfilled, (state, action) => {
        state.status.loading = false;
        state.orders = action.payload.result;
      })
      .addCase(searchOrders.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      })
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status.loading = false;
        state.users = action.payload.result;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      })
      // Add Order
      .addCase(addOrder.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
        state.orderResponse = null;
      })
      .addCase(addOrder.fulfilled, (state, action) => {
        state.status.loading = false;
        state.orderResponse = action.payload.result;
      })
      .addCase(addOrder.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
        state.orderResponse = null;
      })
      // Add Quick Order
      .addCase(addQuickOrder.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
        state.quickOrder = null;
      })
      .addCase(addQuickOrder.fulfilled, (state, action) => {
        state.status.loading = false;
        state.quickOrder = action.payload.result;
      })
      .addCase(addQuickOrder.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
        state.quickOrder = null;
      })
      // Fetch Queue (its own loading flag: it refreshes on its own)
      .addCase(fetchQueue.pending, (state) => {
        state.queueLoading = true;
      })
      .addCase(fetchQueue.fulfilled, (state, action) => {
        state.queueLoading = false;
        state.queue = action.payload.result || [];
      })
      .addCase(fetchQueue.rejected, (state) => {
        state.queueLoading = false;
      })
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status.loading = false;
        state.products = action.payload.result;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      });
  },
});

export const { resetError } = quickOrderSlice.actions;
export default quickOrderSlice.reducer;
